/**
 * Turning a drawn gesture into the thing that gets stored (USR-13).
 *
 * **The stored signature is the pen path, not a picture of it**, and ADR-0055
 * has the argument. The consequence for this module is that everything here is
 * geometry: capture produces points in CSS pixels wherever the pad happened to
 * be laid out, and what leaves is a resolution-independent mark in its own unit
 * box, which a renderer can draw at any size.
 *
 * **All of it is pure, and that is deliberate rather than incidental.** The
 * drawing surface is a canvas, which jsdom cannot give a context for — so a pad
 * that did its own smoothing and normalisation inline would be untestable in
 * the workspace where the tests live. `SignaturePad` owns pointer events and
 * pixels; this owns every decision about what the mark *is*. The seam is the
 * same one `zoomMath.ts` and `pdfPageLayout.ts` draw for the same reason.
 *
 * The three transformations, in order and each for its own reason:
 *
 * 1. **Width from velocity**, because a constant-width stroke reads as a
 *    scribble and a signature is supposed to look like ink. Fast means thin.
 * 2. **Simplify**, because a pointer event stream is far denser than the shape
 *    needs — sixty points a second, most of them on a straight run.
 * 3. **Normalise and quantise**, because the mark has to be independent of the
 *    pad it was drawn on, and because a float has more than one decimal
 *    spelling and the server hashes the canonical form.
 *
 * **Steps 1 and 2 are the mark; step 3 is the record.** `drawnStrokes` stops
 * after 2 and is what a live pad paints, so ink lands where the pen is;
 * `strokesToInk` runs all three and is what gets stored. They were the same
 * call once, which is how the pad came to re-centre itself under the finger.
 *
 * A fourth, `smoothed`, belongs to neither: it is a **rendering** step every
 * drawing surface applies and nothing stores, so the digest never sees it.
 */

/** What the pad hands over: one entry per pointer event, in CSS pixels. */
export interface RawPoint {
  x: number;
  y: number;
  /** Milliseconds, from any monotonic origin. Only differences are read. */
  t: number;
}

/** The wire and storage format. Mirrors `apps/api/src/shared/signatureInk.ts`. */
export interface SignatureInk {
  v: 1;
  /** width / height of the mark's own bounding box. */
  aspect: number;
  /** One flat `[x, y, w, x, y, w, …]` per stroke. */
  strokes: number[][];
}

/** Coordinates run 0–1000 across the mark's own bounding box, on both axes. */
const GRID = 1_000;

/**
 * Pen width is stored as thousandths of the box's **width**, capped at a byte.
 *
 * Width rather than height, and the reason is the degenerate case rather than
 * the ordinary one. A signature is wider than it is tall, so height is the
 * intuitive reference — but somebody who draws a single flat stroke has a mark
 * whose height is barely the nib itself, and a pen measured against that
 * saturates the byte immediately, taking every trace of the velocity shading
 * with it. Against the long axis the ratio stays small in every case the format
 * admits.
 */
const WIDTH_SCALE = 1_000;
const WIDTH_MAX = 255;

/**
 * The band of shapes a mark may have, matching the server's.
 *
 * A gesture outside it is not refused — it is padded into range, because the
 * alternative is a save that fails with a message about aspect ratios for
 * somebody who drew a straight line. `apps/api/src/shared/signatureInk.ts`
 * holds the same two numbers and refuses; keeping them in step by hand is the
 * standing cost of there being no shared types package.
 */
const ASPECT_MIN = 0.2;
const ASPECT_MAX = 30;

/**
 * Nib width at rest, and the narrowest a fast stroke gets, both in CSS pixels.
 *
 * Raised from 2.6/0.9: the thinner pen read as a pencil line rather than a
 * signature, and on a phone it nearly vanished. The ratio between them is what
 * `VELOCITY_WEIGHT` was tuned against, so both moved together.
 */
const NIB_PX = 3.6;
const NIB_MIN_PX = 1.5;

/**
 * How hard speed thins the line.
 *
 * Tuned by drawing: at 1.4 a signature written at an ordinary pace varies
 * between about a third and the full nib, which reads as ink. Higher looks
 * calligraphic and slightly fake; lower loses the effect entirely.
 */
const VELOCITY_WEIGHT = 1.4;

/** How much the width may change between neighbours, as a fraction. */
const WIDTH_SMOOTHING = 0.35;

/**
 * How far a point may sit from the line between its neighbours before it is
 * worth keeping, in CSS pixels.
 *
 * Below a third of a pixel the difference is not drawable; this is a little
 * above that, which typically removes half the samples on a signature and none
 * of its shape.
 */
const SIMPLIFY_EPSILON_PX = 0.45;

/** A drag with no distance is a click, and a click is not a signature. */
const MIN_INK_PX = 6;

export interface WidePoint {
  x: number;
  y: number;
  /** Width in CSS pixels at this point. */
  w: number;
}

/**
 * How many straight segments each curve is flattened into by `smoothed`.
 *
 * Four is where the corners stop being visible at pad size. Higher costs the
 * signed PDF a `drawLine` per sample for no difference anybody can see.
 */
const SMOOTH_SAMPLES = 4;

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Width per point, from how fast the pen was moving into it.
 *
 * The second pass matters as much as the first: raw per-point velocity is
 * jittery — a single slow sample in a fast run produces a blob — so each width
 * is pulled toward its neighbours before it is used.
 */
function widthsFor(points: RawPoint[]): WidePoint[] {
  const raw = points.map((point, index) => {
    const previous = points[index - 1];
    if (previous === undefined) return NIB_PX;

    const elapsed = Math.max(1, point.t - previous.t);
    const speed = distance(point, previous) / elapsed;

    return Math.max(NIB_MIN_PX, NIB_PX / (1 + speed * VELOCITY_WEIGHT));
  });

  const smoothed = raw.map((width, index) => {
    const before = raw[index - 1] ?? width;
    const after = raw[index + 1] ?? width;
    const neighbours = (before + after) / 2;

    return width + (neighbours - width) * WIDTH_SMOOTHING;
  });

  return points.map((point, index) => ({ x: point.x, y: point.y, w: smoothed[index] ?? NIB_PX }));
}

/**
 * Ramer–Douglas–Peucker, iteratively.
 *
 * Iterative rather than recursive because a slow, deliberate signature on a
 * high-frequency pointer can be several thousand samples in one stroke, and the
 * recursive form is one frame per split.
 */
function simplify(points: WidePoint[], epsilon: number): WidePoint[] {
  if (points.length < 3) return points;

  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  const stack: [number, number][] = [[0, points.length - 1]];

  while (stack.length > 0) {
    const span = stack.pop();
    if (span === undefined) break;

    const [start, end] = span;
    const from = points[start];
    const to = points[end];
    if (from === undefined || to === undefined) continue;

    let farthest = -1;
    let farthestDistance = 0;

    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);

    for (let i = start + 1; i < end; i += 1) {
      const point = points[i];
      if (point === undefined) continue;

      // Perpendicular distance to the segment; for a zero-length segment that
      // degenerates to the distance from the point they share.
      const gap =
        length === 0
          ? distance(point, from)
          : Math.abs(dy * point.x - dx * point.y + to.x * from.y - to.y * from.x) / length;

      if (gap > farthestDistance) {
        farthest = i;
        farthestDistance = gap;
      }
    }

    if (farthest > 0 && farthestDistance > epsilon) {
      keep[farthest] = true;
      stack.push([start, farthest], [farthest, end]);
    }
  }

  return points.filter((_, index) => keep[index] === true);
}

/**
 * The gesture as a drawable line, in the coordinates it was drawn in.
 *
 * **Nothing here moves the mark**, and that is the whole point of it being its
 * own function: a pad that paints this paints ink under the pen. `strokesToInk`
 * takes the same output and *then* trims, pads and normalises it, which is
 * exactly right for a record and exactly wrong for a preview — before the split
 * the pad drew through the normalised form on every pointermove, so the first
 * touch became a one-point bounding box scaled to fill the pad and drawn dead
 * centre, and the whole mark rescaled under the finger as it grew.
 */
export function drawnStrokes(raw: RawPoint[][]): WidePoint[][] {
  return raw
    .filter((stroke) => stroke.length > 0)
    .map((stroke) => simplify(widthsFor(stroke), SIMPLIFY_EPSILON_PX));
}

/**
 * The same points, as curves — flattened back into straight segments.
 *
 * A signature is round, and a simplified pointer stream is a polygon: the RDP
 * pass leaves long chords across every curve, and at the nib widths this pen
 * now uses the corners show. The fix is the standard midpoint quadratic — each
 * interior point becomes the control of a curve running between its two
 * neighbouring midpoints — and the reason it lives here rather than in a
 * renderer is that there are **three** renderers (the pad, the overlay in the
 * viewer, the signed PDF), and `pdf-lib` cannot draw a curve at all. Resampling
 * into more, shorter segments leaves all three drawing straight lines with a
 * width each, which they already do, and keeps them from disagreeing.
 *
 * Not part of the record: `strokesToInk` stores the control points, so the wire
 * format and the server's digest are what they always were.
 */
export function smoothed(strokes: WidePoint[][]): WidePoint[][] {
  return strokes.map(smoothStroke);
}

function smoothStroke(points: WidePoint[]): WidePoint[] {
  const first = points[0];
  const last = points[points.length - 1];
  // A dot and a single segment are already exactly themselves.
  if (points.length < 3 || first === undefined || last === undefined) return points;

  const smooth: WidePoint[] = [first];

  for (let i = 1; i < points.length - 1; i += 1) {
    const previous = points[i - 1];
    const control = points[i];
    const next = points[i + 1];
    if (previous === undefined || control === undefined || next === undefined) continue;

    const from = midpoint(previous, control);
    const to = midpoint(control, next);

    // From `t` above zero, because `from` is the previous curve's last sample —
    // the midpoints are what make the joins continuous without a seam point.
    for (let step = 1; step <= SMOOTH_SAMPLES; step += 1) {
      const t = step / SMOOTH_SAMPLES;
      const inverse = 1 - t;

      smooth.push({
        x: inverse * inverse * from.x + 2 * inverse * t * control.x + t * t * to.x,
        y: inverse * inverse * from.y + 2 * inverse * t * control.y + t * t * to.y,
        w: from.w + (to.w - from.w) * t,
      });
    }
  }

  // The first and last points are joined by a straight run to their midpoint,
  // so a stroke still starts and ends exactly where the pen did.
  smooth.push(last);
  return smooth;
}

function midpoint(a: WidePoint, b: WidePoint): WidePoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, w: (a.w + b.w) / 2 };
}

/** The mark and the padded box it was normalised against, in the input's pixels. */
export interface InkWithBox {
  ink: SignatureInk;
  /**
   * The padded, centred bounding box the ink was normalised against, in the
   * **same pixel space the raw points arrived in**. Its aspect equals
   * `ink.aspect`, so a renderer that fits the ink by aspect fills this box
   * exactly — which is what lets a caller map it to a placement rect with no
   * distortion (`DrawLayer` maps it to a `FieldRect` via `rectFromScreen`).
   */
  box: { x: number; y: number; width: number; height: number };
}

/**
 * Everything the pad captured, as the mark that gets stored **and** the box it
 * was measured in.
 *
 * Returns `undefined` when there is nothing worth calling a mark — an empty
 * pad, or a stray tap. The caller renders that as "nothing to save" rather than
 * storing a dot and letting somebody sign a court filing with it.
 *
 * `strokesToInk` is this without the box, for the signature pad that only wants
 * the record; free-draw wants the box too, to place the mark where it was drawn.
 */
export function strokesToInkWithBox(raw: RawPoint[][]): InkWithBox | undefined {
  const withWidths = drawnStrokes(raw);

  if (withWidths.length === 0) return undefined;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const stroke of withWidths) {
    for (const point of stroke) {
      // The box covers the drawn line, not its centre path — otherwise half the
      // nib is clipped at every edge when the mark is placed.
      minX = Math.min(minX, point.x - point.w / 2);
      minY = Math.min(minY, point.y - point.w / 2);
      maxX = Math.max(maxX, point.x + point.w / 2);
      maxY = Math.max(maxY, point.y + point.w / 2);
    }
  }

  const width = maxX - minX;
  const height = maxY - minY;
  if (width < MIN_INK_PX && height < MIN_INK_PX) return undefined;

  /*
    **A flat mark is padded into shape, not refused.** A single horizontal
    stroke has a box whose height is barely the nib, which divides out to an
    aspect ratio in the hundreds — and the server refuses those, so somebody who
    drew a straight line would meet a save that failed with a message about
    aspect ratios. Growing the short axis until the box is placeable, and
    centring the ink in what that produces, keeps the mark exactly as drawn and
    only changes the space around it.
  */
  let boxWidth = Math.max(width, NIB_PX);
  let boxHeight = Math.max(height, NIB_PX);

  if (boxWidth / boxHeight > ASPECT_MAX) boxHeight = boxWidth / ASPECT_MAX;
  if (boxWidth / boxHeight < ASPECT_MIN) boxWidth = boxHeight * ASPECT_MIN;

  // Centre, so the padding above is shared between the two sides rather than
  // pinning the ink to a corner of a box that is taller than it needs.
  const originX = minX - (boxWidth - width) / 2;
  const originY = minY - (boxHeight - height) / 2;

  const strokes = withWidths.map((stroke) =>
    stroke.flatMap((point) => [
      clampInt(((point.x - originX) / boxWidth) * GRID, 0, GRID),
      clampInt(((point.y - originY) / boxHeight) * GRID, 0, GRID),
      clampInt((point.w / boxWidth) * WIDTH_SCALE, 1, WIDTH_MAX),
    ]),
  );

  return {
    ink: { v: 1, aspect: boxWidth / boxHeight, strokes },
    box: { x: originX, y: originY, width: boxWidth, height: boxHeight },
  };
}

/**
 * Everything the pad captured, as the mark that gets stored.
 *
 * Returns `undefined` when there is nothing worth calling a signature — an
 * empty pad, or a stray tap. The caller renders that as "nothing to save"
 * rather than storing a dot and letting somebody sign a court filing with it.
 */
export function strokesToInk(raw: RawPoint[][]): SignatureInk | undefined {
  return strokesToInkWithBox(raw)?.ink;
}

function clampInt(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, Math.round(value)));
}

/**
 * A stored mark, back in drawable coordinates, at the size asked for.
 *
 * The inverse of the normalisation above, and the only way anything renders a
 * signature — the account screen's preview and, later, the mark that goes into
 * a document both come through here, so they cannot disagree about where the
 * ink sits.
 *
 * `smoothed` is applied on the way out, which is why the overlay and the signed
 * PDF got the curves without either of them changing: rendering is the only
 * thing that wants them, and this is the door every renderer comes through.
 */
export function inkToStrokes(ink: SignatureInk, height: number): WidePoint[][] {
  const width = height * ink.aspect;

  const strokes = ink.strokes.map((stroke) => {
    const points: WidePoint[] = [];
    for (let i = 0; i + 2 < stroke.length; i += 3) {
      points.push({
        x: ((stroke[i] ?? 0) / GRID) * width,
        y: ((stroke[i + 1] ?? 0) / GRID) * height,
        // Against the box's width, matching how it was encoded.
        w: ((stroke[i + 2] ?? 1) / WIDTH_SCALE) * width,
      });
    }

    return points;
  });

  return smoothed(strokes);
}
